import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, IconButton, Tooltip, Snackbar, Alert, Button } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Sidebar from "./components/Sidebar";
import Toolbar from "./components/Toolbar";
import RndCanvas from "./components/RndCanvas";
import { generateId } from "./utils/helpers";
import DefaultLogo from "../assets/avatar.png";

// Función para generar números únicos de 6 dígitos con ceros a la izquierda
const generateUniqueNumber = () => {
  // Generar un número aleatorio entre 1 y 999999
  const randomNumber = Math.floor(Math.random() * 999999) + 1;
  // Convertir a string y rellenar con ceros a la izquierda hasta 6 dígitos
  const paddedNumber = randomNumber.toString().padStart(6, "0");
  // Concatenar con el prefijo "NRO #"
  return `NRO #${paddedNumber}`;
};

const DEFAULT_HEADER_LOGO =
  "https://te-ayudo-img.s3.us-east-2.amazonaws.com/production/users/5f2d8809bceaa5798cbf861c@1663253858475";

const FormBuilder = ({ onClose, editingForm }) => {
  // Función para crear componente de cabecera por defecto
  const createDefaultHeader = () => ({
    id: generateId(),
    type: "formHeader",
    logo: DEFAULT_HEADER_LOGO,
    nameLogo: "Nombre del Hospital",
    titleName: "Nueva plantilla",
    number: generateUniqueNumber(), // Generar número único automáticamente
    rndPosition: {
      x: 0,
      y: 0,
      width: 100,
      height: 8,
    },
    style: {
      backgroundColor: "#ffffff",
      borderRadius: 4,
      borderColor: "#e0e0e0",
      borderWidth: 1,
      padding: { top: 8, right: 8, bottom: 8, left: 8 },
      margin: { top: 0, right: 0, bottom: 0, left: 0 },
      textStyle: {
        fontSize: 14,
        fontWeight: "normal",
        color: "#000000",
        textAlign: "left",
        fontFamily: "inherit",
      },
      shadow: {
        enabled: false,
        color: "rgba(0,0,0,0.2)",
        blur: 4,
        offsetX: 0,
        offsetY: 2,
      },
    },
  });

  // Estados principales
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [pages, setPages] = useState([
    {
      components: [createDefaultHeader()],
    },
  ]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [formName, setFormName] = useState("Nueva plantilla");
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedoAction, setIsUndoRedoAction] = useState(false);

  const [copiedComponent, setCopiedComponent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [logoPreview, setLogoPreview] = useState(null);
  const [, setLogoFile] = useState(null);

  // Estados para manejo de imágenes de componentes Image
  const [, setImageFiles] = useState(new Map());
  const [, setImagePreviews] = useState(new Map());

  // Referencias
  const fileInputRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const pageRefs = useRef([]);

  useEffect(() => {
    if (editingForm) {
      setFormName(editingForm.name);
      setPages(editingForm.pages || [{ components: [createDefaultHeader()] }]);
      setHistory([]);
      setHistoryIndex(-1);
    }
  }, [editingForm]);

  // Función para mostrar notificaciones
  const showNotification = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Función para cerrar notificación
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Función para guardar estado en el historial
  const saveToHistory = useCallback(
    (newPages, newFormName) => {
      if (isUndoRedoAction) {
        setIsUndoRedoAction(false);
        return;
      }

      const newState = {
        pages: JSON.parse(JSON.stringify(newPages)),
        formName: newFormName,
        timestamp: Date.now(),
      };

      setHistory((prev) => {
        const newHistory = prev.slice(0, historyIndex + 1);
        newHistory.push(newState);
        // Limitar el historial a 50 estados
        if (newHistory.length > 50) {
          newHistory.shift();
        }
        return newHistory;
      });
      setHistoryIndex((prev) => prev + 1);
    },
    [historyIndex, isUndoRedoAction]
  );

  // Función para actualizar páginas con historial
  const updatePagesWithHistory = useCallback(
    (newPages) => {
      setPages(newPages);
      saveToHistory(newPages, formName);
    },
    [saveToHistory, formName]
  );

  const updateFormNameWithHistory = useCallback(
    (newFormName) => {
      setFormName(newFormName);
      saveToHistory(pages, newFormName);
    },
    [saveToHistory, pages]
  );

  const handleComponentSelect = (component) => {
    setSelectedComponent(component);
  };

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const scrollTop = scrollContainerRef.current.scrollTop;
    const containerHeight = scrollContainerRef.current.clientHeight;
    const scrollCenter = scrollTop + containerHeight / 2;

    let currentPage = 0;
    for (let i = 0; i < pageRefs.current.length; i++) {
      const pageRef = pageRefs.current[i];
      if (pageRef) {
        const pageTop = pageRef.offsetTop;
        const pageHeight = pageRef.offsetHeight;

        if (scrollCenter >= pageTop && scrollCenter <= pageTop + pageHeight) {
          currentPage = i;
          break;
        }
      }
    }

    setCurrentPageIndex(currentPage);
  };

  // Efecto para inicializar las referencias de páginas
  useEffect(() => {
    pageRefs.current = pageRefs.current.slice(0, pages.length);
  }, [pages.length]);

  // Inicializar historial
  useEffect(() => {
    if (history.length === 0) {
      saveToHistory(pages, formName);
    }
  }, []);

  // Funciones de manejo de páginas
  const handleAddPage = () => {
    const newPage = {
      components: [createDefaultHeader()],
    };
    updatePagesWithHistory([...pages, newPage]);
    setSelectedComponent(null);
  };

  // Handler para click en el logo
  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  // Handler para cambio de archivo
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setLogoPreview(ev.target.result);
        // Actualizar el logo del formHeader en pages
        setPages((pages) =>
          pages.map((page) => ({
            ...page,
            components: page.components.map((comp) =>
              comp.type === "formHeader" ? { ...comp, logo: ev.target.result } : comp
            ),
          }))
        );
      };
      reader.readAsDataURL(file);
    }
  };

  // Función para crear un input de archivo temporal para componentes Image
  const createImageInputForComponent = (componentId) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => handleImageChange(e, componentId);
    input.click();
  };

  // Función para comprimir imagen
  const compressImage = (file, maxWidth = 800, maxHeight = 600, quality = 0.7) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Calcular nuevas dimensiones manteniendo aspecto
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        // Dibujar imagen redimensionada
        ctx.drawImage(img, 0, 0, width, height);

        // Convertir a blob comprimido
        canvas.toBlob(resolve, "image/jpeg", quality);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // Handler para cargar imagen en componente Image
  const handleImageChange = (e, componentId) => {
    const file = e.target.files[0];
    if (file) {
      // Comprimir imagen antes de procesarla
      compressImage(file, 800, 600, 0.8).then((compressedBlob) => {
        // Crear archivo comprimido
        const compressedFile = new File([compressedBlob], file.name, {
          type: "image/jpeg",
          lastModified: Date.now(),
        });

        // Guardar el archivo comprimido en el Map
        setImageFiles((prev) => new Map(prev.set(componentId, compressedFile)));

        const reader = new FileReader();
        reader.onload = (ev) => {
          const imageUrl = ev.target.result;

          // Guardar el preview en el Map
          setImagePreviews((prev) => new Map(prev.set(componentId, imageUrl)));

          // Actualizar el componente con la imagen PRESERVANDO todas las propiedades
          setPages((pages) =>
            pages.map((page) => ({
              ...page,
              components: page.components.map((comp) =>
                comp.id === componentId
                  ? {
                      ...comp, // Preservar todas las propiedades originales
                      src: imageUrl,
                      imagePreview: imageUrl,
                    }
                  : comp
              ),
            }))
          );
        };
        reader.readAsDataURL(compressedFile);
      });
    }
  };

  // Función para guardar formulario en el backend
  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("token");

      const cleanPages = pages.map((page) => ({
        ...page,
        components: page.components.map((component) => {
          // Copia solo las propiedades necesarias
          const cleaned = { ...component };
          if (cleaned.type === "formHeader" && cleaned.logo && cleaned.logo.startsWith("data:")) {
            cleaned.logo = "";
          }
          if (cleaned.type === "image" && cleaned.src && cleaned.src.startsWith("data:")) {
            cleaned.src = "";
          }
          // Elimina imagePreview si existe
          if ("imagePreview" in cleaned) {
            delete cleaned.imagePreview;
          }
          // Si tienes otros campos base64, elimínalos aquí
          return cleaned;
        }),
      }));

      const formData = {
        name: formName,
        pages: cleanPages,
        metadata: {
          totalPages: pages.length,
          totalComponents: pages.reduce((total, page) => total + page.components.length, 0),
          createdAt: editingForm ? editingForm.createdAt : new Date().toISOString(),
          lastModified: new Date().toISOString(),
        },
      };

      const formDataToSend = new FormData();
      formDataToSend.append("formData", JSON.stringify(formData));

      // Buscar imágenes en los componentes y agregarlas al FormData
      pages.forEach((page) => {
        page.components.forEach((component) => {
          // Procesar logo del formHeader
          if (component.type === "formHeader" && component.logo && component.logo.startsWith("data:")) {
            // Convertir base64 a archivo
            const base64Data = component.logo.split(",")[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "image/png" });
            formDataToSend.append("logo", blob, "logo.png");
          }

          // Procesar imágenes de componentes Image
          if (component.type === "image" && component.src && component.src.startsWith("data:")) {
            // Convertir base64 a archivo
            const base64Data = component.src.split(",")[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "image/png" });
            formDataToSend.append(`image_${component.id}`, blob, `image_${component.id}.png`);
          }
        });
      });

      const url = editingForm
        ? `${process.env.REACT_APP_API_URL}/enterprise/forms/${editingForm._id}`
        : `${process.env.REACT_APP_API_URL}/enterprise/forms`;

      const response = await fetch(url, {
        method: editingForm ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error("Error al guardar formulario");
      }

      const result = await response.json();
      showNotification("✅ Plantilla guardada exitosamente");

      // Solo redirigir al listado si se está creando un nuevo formulario
      if (!editingForm) {
        onClose();
      }
    } catch (error) {
      showNotification(`❌ Error al guardar: ${error.message}`, "error");
    } finally {
      setSaving(false);
      setLogoPreview(null); // Limpiar preview tras guardar
      setLogoFile(null);
    }
  };

  const handlePreview = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  const handleExport = (format) => {
    if (format === "json") {
      const formData = {
        name: formName,
        pages: pages,
        metadata: {
          totalPages: pages.length,
          totalComponents: pages.reduce((total, page) => total + page.components.length, 0),
          createdAt: new Date().toISOString(),
          lastModified: new Date().toISOString(),
        },
      };

      // Descargar archivo JSON
      const dataStr = JSON.stringify(formData, null, 2);
      const dataUri = "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
      const exportFileDefaultName = `${formName.replace(/\s+/g, "_")}.json`;

      const linkElement = document.createElement("a");
      linkElement.setAttribute("href", dataUri);
      linkElement.setAttribute("download", exportFileDefaultName);
      linkElement.click();
    } else if (format === "pdf") {
      console.log("Exportar como PDF");
    }
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setIsUndoRedoAction(true);
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      setPages(JSON.parse(JSON.stringify(previousState.pages)));
      setFormName(previousState.formName);
      setHistoryIndex(newIndex);
      setSelectedComponent(null);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setIsUndoRedoAction(true);
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      setPages(JSON.parse(JSON.stringify(nextState.pages)));
      setFormName(nextState.formName);
      setHistoryIndex(newIndex);
      setSelectedComponent(null);
    }
  };

  const handleNewForm = () => {
    if (
      window.confirm("¿Estás seguro de que quieres crear una nueva plantilla? Se perderán los cambios no guardados.")
    ) {
      const newPage = {
        components: [createDefaultHeader()],
      };
      const newPages = [newPage];
      setPages(newPages);
      setSelectedComponent(null);
      setFormName("Nueva plantilla");
      // Limpiar historial
      setHistory([]);
      setHistoryIndex(-1);
      saveToHistory(newPages, "Nueva plantilla");
    }
  };

  const handleDeleteForm = () => {
    if (window.confirm("¿Estás seguro de que quieres eliminar esta plantilla?")) {
      const newPage = {
        components: [createDefaultHeader()],
      };
      const newPages = [newPage];
      setPages(newPages);
      setSelectedComponent(null);
      setFormName("Nueva plantilla");
      // Limpiar historial
      setHistory([]);
      setHistoryIndex(-1);
      saveToHistory(newPages, "Nueva plantilla");
    }
  };

  const handleFormNameChange = (name) => {
    updateFormNameWithHistory(name);
  };

  // Función para eliminar componente seleccionado
  const handleDeleteComponent = () => {
    if (selectedComponent) {
      const currentPage = pages[currentPageIndex];
      const newComponents = currentPage.components.filter((comp) => comp.id !== selectedComponent.id);
      const newPages = pages.map((page, index) =>
        index === currentPageIndex ? { ...page, components: newComponents } : page
      );
      updatePagesWithHistory(newPages);
      setSelectedComponent(null);
    }
  };

  // Función para copiar componente
  const handleCopyComponent = () => {
    if (selectedComponent) {
      const componentToCopy = JSON.parse(JSON.stringify(selectedComponent));
      // Generar nuevo ID para el componente copiado
      componentToCopy.id = generateId();
      // Ajustar posición ligeramente para que no se superponga
      if (componentToCopy.rndPosition) {
        componentToCopy.rndPosition.x += 5;
        componentToCopy.rndPosition.y += 5;
      }
      setCopiedComponent(componentToCopy);
      showNotification("Componente copiado al portapapeles");
    }
  };

  // Función para pegar componente
  const handlePasteComponent = () => {
    if (copiedComponent) {
      const currentPage = pages[currentPageIndex];
      const newComponent = JSON.parse(JSON.stringify(copiedComponent));
      // Generar nuevo ID para el componente pegado
      newComponent.id = generateId();
      // Ajustar posición ligeramente
      if (newComponent.rndPosition) {
        newComponent.rndPosition.x += 5;
        newComponent.rndPosition.y += 5;
      }

      const newComponents = [...currentPage.components, newComponent];
      const newPages = pages.map((page, index) =>
        index === currentPageIndex ? { ...page, components: newComponents } : page
      );
      updatePagesWithHistory(newPages);
      setSelectedComponent(newComponent);
      showNotification("Componente pegado exitosamente");
    }
  };

  // Funciones de importación/exportación
  const handleImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const formData = JSON.parse(e.target.result);

          if (formData.pages) {
            // Limpiar estructura de páginas y componentes importados
            const cleanedPages = formData.pages.map((page) => ({
              components: (page.components || []).map((comp) => {
                if (comp.type === "formHeader") {
                  return {
                    ...comp,
                    logo: comp.logo !== undefined && comp.logo !== "" ? comp.logo : DEFAULT_HEADER_LOGO,
                    nameLogo: comp.nameLogo !== undefined && comp.nameLogo !== "" ? comp.nameLogo : "Nombre del logo",
                    titleName:
                      comp.titleName !== undefined && comp.titleName !== "" ? comp.titleName : "Nueva plantilla",
                  };
                }
                return comp;
              }),
            }));
            setFormName(formData.name);
            setPages(cleanedPages);
            setSelectedComponent(null);
            // Limpiar historial
            setHistory([]);
            setHistoryIndex(-1);
            saveToHistory(cleanedPages, formData.name);
          } else if (formData.components) {
            // Verificar si ya tiene un componente de cabecera
            const hasHeader = formData.components.some((comp) => comp.type === "formHeader");
            let components = formData.components || [];
            // Si no tiene cabecera, agregar una por defecto
            if (!hasHeader) {
              components = [createDefaultHeader(), ...components];
            }
            // Limpiar componentes
            components = components.map((comp) => {
              if (comp.type === "formHeader") {
                return {
                  ...comp,
                  logo: comp.logo !== undefined && comp.logo !== "" ? comp.logo : DEFAULT_HEADER_LOGO,
                  nameLogo: comp.nameLogo !== undefined && comp.nameLogo !== "" ? comp.nameLogo : "Nombre del logo",
                  titleName: comp.titleName !== undefined && comp.titleName !== "" ? comp.titleName : "Nueva plantilla",
                };
              }
              return comp;
            });
            const newPage = {
              components: components,
            };
            const newPages = [newPage];
            setFormName(formData.name);
            setPages(newPages);
            setSelectedComponent(null);
            // Limpiar historial
            setHistory([]);
            setHistoryIndex(-1);
            saveToHistory(newPages, formData.name);
          } else {
            alert("Formato de archivo no válido");
          }
        } catch (error) {
          alert("Error al importar el formulario");
        }
      };
      reader.readAsText(file);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  // Atajos de teclado mejorados
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case "s":
            e.preventDefault();
            handleSave();
            break;
          case "z":
            e.preventDefault();
            if (e.shiftKey) {
              handleRedo();
            } else {
              handleUndo();
            }
            break;
          case "y":
            e.preventDefault();
            handleRedo();
            break;
          case "c":
            e.preventDefault();
            handleCopyComponent();
            break;
          case "v":
            e.preventDefault();
            handlePasteComponent();
            break;
          case "o":
            e.preventDefault();
            handleImportClick();
            break;
        }
      } else if (e.key === "Delete" && selectedComponent) {
        e.preventDefault();
        handleDeleteComponent();
      } else if (e.key === "Escape") {
        setSelectedComponent(null);
      } else if (e.key === "F2") {
        e.preventDefault();
        handleAddPage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedComponent, historyIndex, history.length, copiedComponent]);

  return (
    <Box sx={{ height: "100vh", bgcolor: "white", display: "flex", flexDirection: "column" }}>
      {/* Toolbar fijo */}
      <Box sx={{ flexShrink: 0 }}>
        <Toolbar
          onSave={handleSave}
          onPreview={handlePreview}
          onExport={handleExport}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={historyIndex > 0}
          canRedo={historyIndex < history.length - 1}
          onNewForm={handleNewForm}
          onDeleteForm={handleDeleteForm}
          formName={formName}
          onFormNameChange={handleFormNameChange}
          onAddPage={handleAddPage}
          totalPages={pages.length}
          currentPageIndex={currentPageIndex}
          onCopyComponent={handleCopyComponent}
          onPasteComponent={handlePasteComponent}
          canCopy={!!selectedComponent}
          canPaste={!!copiedComponent}
          saving={saving}
          extraLeft={
            <Button startIcon={<ArrowBackIcon />} onClick={onClose} sx={{ mr: 2 }}>
              Volver
            </Button>
          }
          extraRight={
            <Tooltip
              title={
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                    Atajos de teclado:
                  </Typography>
                  <Typography variant="caption" display="block">
                    Ctrl+S: Guardar
                  </Typography>
                  <Typography variant="caption" display="block">
                    Ctrl+Z: Deshacer
                  </Typography>
                  <Typography variant="caption" display="block">
                    Ctrl+Y / Ctrl+Shift+Z: Rehacer
                  </Typography>
                  <Typography variant="caption" display="block">
                    Ctrl+C: Copiar componente
                  </Typography>
                  <Typography variant="caption" display="block">
                    Ctrl+V: Pegar componente
                  </Typography>
                  <Typography variant="caption" display="block">
                    F2: Nueva página
                  </Typography>
                  <Typography variant="caption" display="block">
                    Ctrl+O: Importar
                  </Typography>
                  <Typography variant="caption" display="block">
                    Delete: Eliminar componente
                  </Typography>
                  <Typography variant="caption" display="block">
                    Escape: Deseleccionar
                  </Typography>
                </>
              }
              placement="bottom"
              arrow
            >
              <IconButton size="small" sx={{ ml: 1 }}>
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
          }
        />
      </Box>

      {/* Contenido principal con layout fijo */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Área de contenido scrolleable */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Contenedor scrolleable para todas las páginas */}
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              overflow: "auto",
              p: 3,
              bgcolor: "#f1f3f4", // Color de fondo como Google Docs
              minWidth: "fit-content", // Asegurar que el contenido no se comprima
              display: "flex",
              flexDirection: "column",
              alignItems: "center", // Centrar las páginas horizontalmente
            }}
          >
            {pages.map((page, index) => (
              <Box
                key={page.id || index}
                ref={(el) => (pageRefs.current[index] = el)}
                sx={{
                  marginBottom: "80px !important",
                  position: "relative",
                  width: "895px", // Ancho fijo basado en BASE_WIDTH del RndCanvas
                  margin: "0 auto 80px auto",
                  bgcolor: "white",
                  borderTopLeftRadius: 16,
                  borderTopRightRadius: 16,
                  borderBottomLeftRadius: 16,
                  borderBottomRightRadius: 16,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  border: "1px solid #e0e0e0",
                  flexShrink: 0, // Evitar que se comprima
                  "&:last-child": {
                    marginBottom: "0 !important",
                  },
                }}
              >
                {/* Indicador de página */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -30,
                    left: "50%",
                    transform: "translateX(-50%)",
                    bgcolor: "white",
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                    fontSize: "0.875rem",
                    color: "text.secondary",
                    zIndex: 10,
                  }}
                >
                  Página {index + 1} de {pages.length}
                </Box>

                <RndCanvas
                  components={page.components}
                  onComponentUpdate={(id, updates) => {
                    const newComponents = page.components.map((comp) =>
                      comp.id === id ? { ...comp, ...updates } : comp
                    );
                    const newPages = pages.map((p, i) => (i === index ? { ...p, components: newComponents } : p));
                    updatePagesWithHistory(newPages);
                  }}
                  onComponentDelete={(id) => {
                    const newComponents = page.components.filter((comp) => comp.id !== id);
                    const newPages = pages.map((p, i) => (i === index ? { ...p, components: newComponents } : p));
                    updatePagesWithHistory(newPages);
                    if (selectedComponent?.id === id) {
                      setSelectedComponent(null);
                    }
                  }}
                  selectedComponent={selectedComponent}
                  onComponentSelect={handleComponentSelect}
                  onDrop={(newComponent) => {
                    // Si es un componente Image, agregar la función onImageClick
                    if (newComponent.type === "image") {
                      newComponent.onImageClick = () => createImageInputForComponent(newComponent.id);
                    }

                    const newComponents = [...page.components, newComponent];
                    const newPages = pages.map((p, i) => (i === index ? { ...p, components: newComponents } : p));
                    updatePagesWithHistory(newPages);
                    setSelectedComponent(newComponent);
                  }}
                  isPreviewMode={isPreviewMode}
                  formHeader={{
                    name: page.formName || "Nueva plantilla",
                    number: page.formNumber || "NRO 002517",
                    logo: page.logo || "",
                    logoPreview: logoPreview,
                    onLogoClick: handleLogoClick,
                  }}
                  onHeaderUpdate={(updates) => {
                    const newPages = pages.map((p, i) => (i === index ? { ...p, ...updates } : p));
                    updatePagesWithHistory(newPages);
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Sidebar fijo */}
        <Box sx={{ flexShrink: 0, position: "relative" }}>
          {/* Botón para abrir/cerrar sidebar al lado del sidebar derecho */}
          <IconButton
            onClick={() => setSidebarOpen(!sidebarOpen)}
            sx={{
              position: "absolute",
              top: 16,
              left: -40,
              zIndex: 50,
              bgcolor: "white",
              border: "1px solid",
              borderColor: "grey.300",
              "&:hover": { bgcolor: "grey.50" },
            }}
          >
            {sidebarOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
          <Sidebar
            isOpen={sidebarOpen}
            selectedComponent={selectedComponent}
            onComponentSelect={handleComponentSelect}
            onComponentUpdate={(id, updates) => {
              // Actualizar el componente en la página actual
              const newComponents = pages[currentPageIndex].components.map((comp) =>
                comp.id === id ? { ...comp, ...updates } : comp
              );
              const newPages = pages.map((p, i) => (i === currentPageIndex ? { ...p, components: newComponents } : p));
              updatePagesWithHistory(newPages);
              // Actualizar el componente seleccionado
              setSelectedComponent((prev) => (prev?.id === id ? { ...prev, ...updates } : prev));
            }}
          />
        </Box>
      </Box>

      {/* Input oculto para importar archivos */}
      <input type="file" accept="image/*" style={{ display: "none" }} ref={fileInputRef} onChange={handleLogoChange} />

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FormBuilder;
